import { CacheProvider, EmotionCache } from '@emotion/react';
import { FC, useEffect, useRef } from 'react';
import { Hydrate, QueryClientProvider } from '@tanstack/react-query';
import { useAuth, useDimmedLayer, useUnderMaintenance } from '@/logic/state';

import type { AppProps } from 'next/app';
import { CssBaseline } from '@mui/material';
import { LanguageProvider } from '../lang';
import NProgress from 'nprogress';
import { QueryClientHandelErrorHook } from '@/hooks/use-query-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import TagManager from 'react-gtm-module';
import ThemeConfig from '../theme';
import UnderMaintenance from '../components/under-maintenance';
import createEmotionCache from '../utility/createEmotionCache';
import getConfig from 'next/config';
import shallow from 'zustand/shallow';
import { useRouter } from 'next/router';
import { Layout } from '@/components/layout/layout';

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

const clientSideEmotionCache = createEmotionCache();

const MyApp: FC<MyAppProps> = props => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  const router = useRouter();
  const { queryClient } = QueryClientHandelErrorHook();
  const { publicRuntimeConfig } = getConfig();
  const underMaintenanceMode = useUnderMaintenance(s => s.underMaintenanceMode, shallow);
  const setDimmedLayer = useDimmedLayer(s => s.setDimmedLayer, shallow);
  const user = useAuth(s => s.user, shallow);
  const gtmInitialized = useRef(false);
  const hasHandledFirstToken = useRef(false);

  NProgress.configure({
    showSpinner: false,
  });

  // `router` used to be a dependency here. Its identity changes on every
  // navigation, so TagManager injected a fresh GTM <script> per route change —
  // each one re-downloading and re-executing the container on the main thread.
  //
  // Initializing eagerly on mount put GTM's ~365ms of scripting time (measured:
  // bootup-time audit) right in the same window as hydration finishing and the
  // hero image's LCP paint — all three competing for the main thread at once.
  // Deferring via requestIdleCallback lets LCP land first; the 2s setTimeout
  // fallback covers Safari, which has no requestIdleCallback.
  useEffect(() => {
    const gtmId = publicRuntimeConfig?.googleTagManagerId;
    if (!gtmId || gtmInitialized.current) return;

    const initGtm = () => {
      if (gtmInitialized.current) return;
      gtmInitialized.current = true;
      TagManager.initialize({ gtmId });
    };

    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(initGtm, { timeout: 2000 });
      return () => window.cancelIdleCallback(handle);
    }

    const timer = setTimeout(initGtm, 2000);
    return () => clearTimeout(timer);
  }, [publicRuntimeConfig?.googleTagManagerId]);

  // This function will invalidate all queries when the user token changes
  useEffect(() => {
    const token = user?.token;
    const isFirstRun = !hasHandledFirstToken.current;
    hasHandledFirstToken.current = true;

    // On the first run with no token there is nothing to invalidate: the
    // anonymous data prefetched in getServerSideProps is already the data this
    // visitor should see, and invalidating would refetch every query on load
    // for nothing. A token that appears later (restored from storage, or a
    // login) still invalidates, because getServerSideProps runs without an
    // Authorization header and its data is not personalised.
    if (isFirstRun && !token) return;

    queryClient.invalidateQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token]);

  useEffect(() => {
    const handleRouteStart = () => {
      setDimmedLayer(true);
      NProgress.start();
    };
    const handleRouteDone = () => {
      setDimmedLayer(false);
      NProgress.done();
    };

    router.events.on('routeChangeStart', handleRouteStart);
    router.events.on('routeChangeComplete', handleRouteDone);
    router.events.on('routeChangeError', handleRouteDone);
    return () => {
      router.events.off('routeChangeStart', handleRouteStart);
      router.events.off('routeChangeComplete', handleRouteDone);
      router.events.off('routeChangeError', handleRouteDone);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events, router.pathname]);

  // Recover from stale-deploy chunk-load failures: a client still running the
  // previous build requests a JS/CSS chunk that the new deploy removed, so it
  // 404s. Hard-reload once to fetch fresh HTML that references current chunks.
  useEffect(() => {
    const RELOAD_KEY = 'chunkReloadAt';
    const RELOAD_WINDOW = 10000; // ms — guard against reload loops

    const looksLikeChunkError = (input?: unknown): boolean => {
      if (!input) return false;
      const err = input as { name?: string; message?: string };
      const text = `${err?.name ?? ''} ${err?.message ?? input}`;
      return /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed/i.test(text);
    };

    const reloadOnce = () => {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      if (Date.now() - last < RELOAD_WINDOW) return; // just reloaded — don't loop
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
    };

    const onError = (e: ErrorEvent) => {
      const target = e.target as (HTMLScriptElement & HTMLLinkElement) | null;
      const src = target?.src || target?.href || '';
      if (looksLikeChunkError(e.error || e.message) || /\/_next\/static\//.test(src)) {
        reloadOnce();
      }
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      // Next.js pages-router rejects with these when a navigation is superseded
      // by another one — benign, so silence the "Uncaught (in promise)" noise.
      const reason = e.reason as { message?: string } | undefined;
      const reasonText = String(reason?.message ?? reason ?? '');
      if (
        reasonText.includes('routeChange aborted') ||
        reasonText.includes('Route Cancelled') ||
        reasonText.includes('Abort fetching component')
      ) {
        e.preventDefault();
        return;
      }
      if (looksLikeChunkError(e.reason)) reloadOnce();
    };
    const onRouteError = (err: unknown) => {
      if (looksLikeChunkError(err)) reloadOnce();
    };

    // capture phase also catches <script>/<link> resource load (404) failures
    window.addEventListener('error', onError, true);
    window.addEventListener('unhandledrejection', onRejection);
    router.events.on('routeChangeError', onRouteError);
    return () => {
      window.removeEventListener('error', onError, true);
      window.removeEventListener('unhandledrejection', onRejection);
      router.events.off('routeChangeError', onRouteError);
    };
  }, [router.events]);

  return (
    <CacheProvider value={emotionCache}>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={(pageProps as any).dehydratedState}>
          <LanguageProvider serverMessages={(pageProps as any).intlMessages}>
            <ThemeConfig>
              {/*
                  GA4 used to be loaded twice: once here via gtag/js, and again
                  by the GTM container, which carries its own GA4 config tag for
                  the same measurement id (G-8P6V4NP26Z, 29 references in
                  GTM-KNDFGVN). That cost ~168 KiB of duplicate download and
                  parse on every page load, and double-counted nothing only
                  because GA4 dedupes by id.

                  GTM is now the single loader. TagManager.initialize() above is
                  what boots it.

                  ponytail: the UAT container (GTM-M9WNLX6S) has no GA4 tag, so
                  UAT reports nothing until one is added there. Prod is
                  unaffected.
                */}
              <CssBaseline />

              {underMaintenanceMode?.isActive ? (
                <UnderMaintenance />
              ) : (
                <Layout>
                  <Component key={router.asPath} {...pageProps} />
                </Layout>
              )}
            </ThemeConfig>
          </LanguageProvider>
        </Hydrate>
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
      </QueryClientProvider>
    </CacheProvider>
  );
};

export default MyApp;
